import React, { useMemo, useState, useEffect } from "react";
import { FaCheck, FaClock } from "react-icons/fa";

/* ================= TYPES ================= */

interface ValuationStep {
  id: number;
  name: string;
  completed: boolean;
  inProgress: boolean;
}

/* ================= STEPS ================= */

const STEPS = [
  { id: 1, name: "Submit Property" },
  { id: 2, name: "Payment" },
  { id: 3, name: "Data Collection" },
  { id: 4, name: "Professional Opinion" },
  { id: 5, name: "Preview Valuation" },
  { id: 6, name: "Final Report Edit" },
  { id: 7, name: "Completed" },
];

/* ================= HELPERS ================= */

function getRemainingTime(timeRemaining?: string): string {
  if (!timeRemaining) return "";

  const now = new Date();
  const endTime = new Date(timeRemaining);
  const diffMs = endTime.getTime() - now.getTime();

  if (diffMs <= 0) return "Done";

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}min left`;
  return `${minutes} min left`;
}

/* ================= CLIENT COMPONENT ================= */

const PropertyValuationTrackerClient: React.FC<{ propertyId: string }> = ({
  propertyId,
}) => {
  const [property, setProperty] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState<string | undefined>(undefined);
  const [now, setNow] = useState(new Date());

  // Fetch property data from API
  useEffect(() => {
    const fetchProperty = async () => {
      if (!propertyId) return;
      setIsLoading(true);
      setErrorMessage("");
      try {
        console.log("Fetching property data for ID:", propertyId);
        const Valuation_token = localStorage.getItem("valuation_token");
        const BASE_URL =
          import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";
        const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Valuation_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch property: ${response.statusText}`);
        }

        const result = await response.json();
        setProperty(result.data);
        
        // Set step and time remaining from fetched data
        setCurrentStep(result.data?.valuationStep?.step || 0);
        setTimeRemaining(result.data?.valuationStep?.timeRemaining);
      } catch (error) {
        console.error("Error fetching property:", error);
        setErrorMessage("Failed to load property details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProperty();
  }, [propertyId]);

  // Update clock every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Sync with property changes
  useEffect(() => {
    if (property) {
      setCurrentStep(property.valuationStep?.step || 0);
      setTimeRemaining(property.valuationStep?.timeRemaining);
    }
  }, [property]);

  const steps: ValuationStep[] = useMemo(() => {
    return STEPS.map((s) => ({
      ...s,
      completed: s.id < currentStep,
      inProgress: s.id === currentStep,
    }));
  }, [currentStep]);

  const completedCount = steps.filter((s) => s.completed).length;
  const totalSteps = steps.length;
  const hasActiveStep = steps.some((s) => s.inProgress);

  const progress =
    totalSteps === 0
      ? 0
      : Math.round(
          ((completedCount + (hasActiveStep ? 0.5 : 0)) / totalSteps) * 100
        );

  const remaining = hasActiveStep ? getRemainingTime(timeRemaining) : "";

  
// Progress: 100% when user reaches the last step
const isOnLastStep = currentStep === totalSteps;
const progressbar =
  totalSteps === 0
    ? 0
    : isOnLastStep
      ? 100
      : Math.round(
          (completedCount + (hasActiveStep ? 0.5 : 0)) / totalSteps * 100
        );

const remainingbar = hasActiveStep ? getRemainingTime(timeRemaining) : "";

  // Show loading state
  if (isLoading) {
    return (
      <div className="bg-gray-50 p-6 font-sans">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-600">Loading valuation progress...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (errorMessage) {
    return (
      <div className="bg-gray-50 p-6 font-sans">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-red-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  // Show "no data" state
  if (!property) {
    return (
      <div className="bg-gray-50 p-6 font-sans">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-600">No property data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6 font-sans">
      <div className="max-w-6xl mx-auto">
        {/* Timeline */}
        <div className="mb-0">
          <div className="relative flex items-start justify-between w-full">
            {/* Background track */}
            <div className="absolute top-[20px] ml-10 left-0 right-0 mr-5 h-1 bg-gray-200 rounded-full" />

            {/* Progress fill */}
            <div
              className="absolute top-[20px] ml-10 left-0 h-1 bg-blue-600 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />

            {steps.map((step, idx) => {
              const isLast = idx === steps.length - 1;
              const isDone = step.completed;
              const isActive = step.inProgress;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center relative z-10">
                    <div
                      className={`
                        w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold shadow-md
                        transition-all duration-300
                        ${
                          isDone
                            ? "bg-green-600"
                            : isActive
                              ? "bg-blue-600 ring-4 ring-blue-100"
                              : "bg-gray-300 text-gray-600"
                        }
                      `}
                    >
                      {isDone ? <FaCheck /> : idx + 1}
                    </div>

                    <div className="mt-3 top-8 absolute text-xs font-medium text-gray-800 whitespace-nowrap">
                      {step.name}
                    </div>

                    {isActive && remaining && (
                      <div className="mt-7 text-xs text-blue-700 font-medium flex items-center gap-1.5">
                        <FaClock size={13} />
                        {remaining}
                      </div>
                    )}
                  </div>

                  {!isLast && (
                    <div className="flex-1 h-[2px] mx-3 mt-[20px] bg-gray-200" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mt-2 text-center">
          <div className="text-2xl font-bold text-blue-600 mb-1">
            {progressbar}%
          </div>
          <div className="text-sm text-gray-600">Overall Progress</div>
        </div>

    </div>
  );
};

export default PropertyValuationTrackerClient;